import { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Calendar, Check, Crown, LineChart, Lock } from "lucide-react";
import { loadStripe, type Stripe as StripeJs, type StripeElementsOptions } from "@stripe/stripe-js";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import Button from "../../components/ui/Button";
import { fieldErrorClassName, labelClassName } from "./signupShared";
import type { SubscriptionPlan } from "../../types/auth";

interface Props {
  defaultPlan?: SubscriptionPlan;
  onComplete: () => Promise<void>;
  onBack: () => void;
}

interface PlanOption {
  id: SubscriptionPlan;
  name: string;
  price: string;
  features: string[];
  Icon: typeof LineChart;
  iconBg: string;
  badge?: string;
}

interface PaymentIntentResponse {
  clientSecret: string;
  publishableKey: string;
}

const planOptions: PlanOption[] = [
  {
    id: "starter",
    name: "Starter",
    price: "$20/month",
    Icon: LineChart,
    iconBg: "bg-gradient-to-br from-sky-500 to-indigo-500",
    features: ["Up to 2 integrations", "1 user account", "AI financial insights"],
  },
  {
    id: "professional",
    name: "Professional",
    price: "$29/month",
    Icon: Crown,
    iconBg: "bg-gradient-to-br from-emerald-500 to-teal-500",
    badge: "POPULAR",
    features: ["Unlimited integrations", "Up to 4 team members", "Advanced reporting"],
  },
];

const readThemeVar = (name: string, fallback: string): string => {
  if (typeof window === "undefined") return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const buildCardElementStyle = () => ({
  style: {
    base: {
      iconColor: readThemeVar("--theme-text-subtle", "#94a3b8"),
      color: readThemeVar("--theme-text-strong", "#0f172a"),
      fontFamily: "ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
      fontSize: "14px",
      "::placeholder": {
        color: readThemeVar("--theme-text-subtle", "#94a3b8"),
      },
    },
    invalid: {
      iconColor: "#e11d48",
      color: "#e11d48",
    },
  },
});

const cardFieldClassName =
  "h-12 w-full rounded-xl border border-slate-300 bg-white px-4 flex items-center transition focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-100";

const PaymentForm = ({ plan, clientSecret, onPlanChange, onComplete, onBack }: { plan: SubscriptionPlan; clientSecret: string; onPlanChange: (plan: SubscriptionPlan) => void; onComplete: () => Promise<void>; onBack: () => void; }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardholderName, setCardholderName] = useState("");
  const cardElementBaseStyle = useMemo(() => buildCardElementStyle(), []);

  const submit = async () => {
    if (!stripe || !elements) {
      setError("Payment form is still loading. Please try again in a moment.");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);
    if (!cardNumberElement) {
      setError("Card details are unavailable. Please refresh and try again.");
      return;
    }

    setError(null);
    setIsSubmitting(true);

    const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
      payment_method: {
        card: cardNumberElement,
        billing_details: cardholderName.trim() ? { name: cardholderName.trim() } : undefined,
      },
    });

    if (confirmError || !setupIntent || setupIntent.status !== "succeeded" || typeof setupIntent.payment_method !== "string") {
      setError(confirmError?.message ?? "Unable to verify your card. Please check the details and try again.");
      setIsSubmitting(false);
      return;
    }

    const response = await fetch("/api/onboarding/payment", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ plan, paymentMethodId: setupIntent.payment_method }),
    });

    if (!response.ok) {
      const result = await response.json().catch(() => null) as { error?: string } | null;
      setError(result?.error ?? "Unable to start your free trial.");
      setIsSubmitting(false);
      return;
    }

    await onComplete();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
        <Calendar className="mt-0.5 h-5 w-5 text-emerald-600" />
        <div>
          <p className="text-sm font-semibold text-emerald-700">1-Day Free Trial</p>
          <p className="text-xs text-emerald-600">No charges for 24 hours. Cancel anytime during trial period.</p>
        </div>
      </div>

      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-slate-900">Choose Your Plan</h3>
        <div className="space-y-3">
          {planOptions.map(option => {
            const isSelected = option.id === plan;
            return (
              <button
                key={option.id}
                type="button"
                onClick={() => onPlanChange(option.id)}
                className={`relative w-full rounded-xl border p-4 text-left transition ${isSelected ? "border-indigo-500 bg-indigo-50/40 ring-2 ring-indigo-100" : "border-slate-200 bg-white hover:border-slate-300"}`}
              >
                {option.badge && (
                  <span className="absolute -top-2 right-4 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    {option.badge}
                  </span>
                )}
                <div className="flex items-start gap-3">
                  <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white ${option.iconBg}`}>
                    <option.Icon className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{option.name}</p>
                        <p className="text-sm text-slate-600">{option.price}</p>
                      </div>
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full border ${isSelected ? "border-indigo-500 bg-indigo-500 text-white" : "border-slate-300 bg-white"}`}>
                        {isSelected && <Check className="h-3 w-3" />}
                      </span>
                    </div>
                    <ul className="mt-3 space-y-1.5">
                      {option.features.map(feature => (
                        <li key={feature} className="flex items-center gap-2 text-xs text-slate-600">
                          <Check className="h-3.5 w-3.5 text-emerald-500" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-900">Credit Card Information</h3>
        </div>

        <div className="space-y-2">
          <label className={labelClassName} htmlFor="card-name">Cardholder Name</label>
          <input
            id="card-name"
            type="text"
            autoComplete="cc-name"
            placeholder="Name on card"
            value={cardholderName}
            onChange={event => setCardholderName(event.target.value)}
            className="h-12 w-full rounded-xl border border-slate-300 bg-white px-4 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
        </div>

        <div className="space-y-2">
          <label className={labelClassName}>Card Number</label>
          <div className={cardFieldClassName}>
            <CardNumberElement options={{ ...cardElementBaseStyle, showIcon: true, placeholder: "1234 1234 1234 1234" }} className="w-full" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <label className={labelClassName}>Expiry</label>
            <div className={cardFieldClassName}>
              <CardExpiryElement options={{ ...cardElementBaseStyle, placeholder: "MM / YY" }} className="w-full" />
            </div>
          </div>
          <div className="space-y-2">
            <label className={labelClassName}>CVV</label>
            <div className={cardFieldClassName}>
              <CardCvcElement options={{ ...cardElementBaseStyle, placeholder: "CVC" }} className="w-full" />
            </div>
          </div>
        </div>

        {error && <p className={fieldErrorClassName}>{error}</p>}
        <p className="text-xs text-slate-500">Your card will be verified but not charged during the trial period.</p>
      </div>

      <div className="space-y-3">
        <Button type="button" className="h-12 w-full" onClick={() => void submit()} disabled={isSubmitting || !stripe || !elements}>
          {isSubmitting ? "Starting trial..." : "Start Free Trial"}
        </Button>
        <Button
          type="button"
          variant="outline"
          className="h-12 w-full border-slate-300 bg-white text-sm font-semibold text-slate-700"
          onClick={onBack}
          disabled={isSubmitting}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Accounting
        </Button>
      </div>
    </div>
  );
};

export default ({ defaultPlan = "starter", onComplete, onBack }: Props) => {
  const [plan, setPlan] = useState<SubscriptionPlan>(defaultPlan);
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupError, setSetupError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const response = await fetch("/api/onboarding/payment/intent", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ plan }),
      });

      if (cancelled) return;

      if (!response.ok) {
        const result = await response.json().catch(() => null) as { error?: string } | null;
        setSetupError(result?.error ?? "Unable to initialize payment form.");
        return;
      }

      const result = await response.json() as PaymentIntentResponse;
      if (cancelled) return;

      setStripePromise(loadStripe(result.publishableKey));
      setClientSecret(result.clientSecret);
    })();

    return () => {
      cancelled = true;
    };
    // We only initialize the SetupIntent once on mount; plan changes are sent on submit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const elementsOptions = useMemo<StripeElementsOptions | null>(() => {
    if (!clientSecret) return null;

    const isDarkSurface = (() => {
      if (typeof window === "undefined") return false;
      const surface = readThemeVar("--theme-bg-panel", "#ffffff");
      const match = surface.match(/^#([0-9a-f]{6})$/i);
      if (!match) return false;
      const hex = match[1]!;
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return (r * 0.299 + g * 0.587 + b * 0.114) < 128;
    })();

    return {
      clientSecret,
      appearance: {
        theme: isDarkSurface ? "night" : "stripe",
        variables: {
          colorPrimary: "#4f46e5",
          colorText: readThemeVar("--theme-text-strong", "#0f172a"),
          colorBackground: readThemeVar("--theme-bg-panel", "#ffffff"),
        },
      },
    };
  }, [clientSecret]);

  if (setupError) {
    return (
      <div className="space-y-4">
        <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">{setupError}</div>
        <Button type="button" variant="outline" className="h-12 w-full border-slate-300 bg-white text-sm font-semibold text-slate-700" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" />
          Back to Accounting
        </Button>
      </div>
    );
  }

  if (!stripePromise || !elementsOptions) {
    return (
      <div className="flex h-48 items-center justify-center text-sm font-medium text-slate-500">
        Loading secure payment form...
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={elementsOptions}>
      <PaymentForm plan={plan} clientSecret={clientSecret!} onPlanChange={setPlan} onComplete={onComplete} onBack={onBack} />
    </Elements>
  );
};
